import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';
import ApiService from '../services/apiService';

interface Claim {
  claimId: string;
  policyNumber: string;
  type: string;
  status: string;
  incidentDate: string;
  incidentDescription: string;
  claimAmount: number;
  estimatedAmount: number;
  approvedAmount?: number;
  priority: string;
  adjusterName?: string;
  dateSubmitted: string;
  dateReviewed?: string;
  dateResolved?: string;
  estimatedResolutionDate: string;
  notes: Array<{
    id: string;
    content: string;
    authorName: string;
    timestamp: string;
    type: string;
  }>;
}

const ClaimsHistory: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchClaims();
    }
  }, [isAuthenticated]);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getUserClaims();

      if (response.success && response.data) {
        setClaims(response.data);
      } else {
        setError(response.error || 'Failed to fetch claims');
      }
    } catch (err) {
      setError('Failed to fetch claims');
      console.error('Error fetching claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return '#007bff';
      case 'under-review': return '#ffc107';
      case 'approved': return '#28a745';
      case 'denied': return '#dc3545';
      case 'closed': return '#6c757d';
      default: return '#007bff';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return '#28a745';
      case 'normal': return '#007bff';
      case 'high': return '#fd7e14';
      case 'urgent': return '#dc3545';
      default: return '#007bff';
    }
  };

  const getTypeDisplay = (type: string) => {
    switch (type) {
      case 'auto': return 'Auto Insurance';
      case 'home': return 'Home Insurance';
      case 'life': return 'Life Insurance';
      case 'health': return 'Health Insurance';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'submitted': return 25;
      case 'under-review': return 50;
      case 'approved': return 100;
      case 'denied': return 100;
      case 'closed': return 100;
      default: return 0;
    }
  };

  return (
    <ProtectedRoute>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '2rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              color: 'white',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2.5rem' }}>
                My Insurance Claims
              </h1>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Track and manage your insurance claim submissions
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '2rem' }}>
              {loading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem',
                  color: '#666'
                }}>
                  <div style={{
                    fontSize: '1.2rem',
                    marginBottom: '1rem'
                  }}>
                    Loading your claims...
                  </div>
                </div>
              ) : error ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem'
                }}>
                  <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    {error}
                  </div>
                  <button
                    onClick={fetchClaims}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              ) : claims.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem',
                  color: '#666'
                }}>
                  <h3>No claims found</h3>
                  <p>You haven't submitted any insurance claims yet.</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: '1.5rem'
                }}>
                  {claims.map((claim) => (
                    <div key={claim.claimId} style={{
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }}>
                      {/* Claim Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '0.5rem'
                          }}>
                            <h3 style={{
                              margin: 0,
                              color: '#333',
                              fontSize: '1.25rem'
                            }}>
                              {getTypeDisplay(claim.type)} Claim
                            </h3>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: 'white',
                              backgroundColor: getStatusColor(claim.status),
                              textTransform: 'capitalize'
                            }}>
                              {claim.status.replace('-', ' ')}
                            </span>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: 'white',
                              backgroundColor: getPriorityColor(claim.priority),
                              textTransform: 'capitalize'
                            }}>
                              {claim.priority} Priority
                            </span>
                          </div>
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#666'
                          }}>
                            Policy: {claim.policyNumber} • Claim ID: {claim.claimId.substring(0, 8)}...
                          </div>
                        </div>
                        <div style={{
                          textAlign: 'right'
                        }}>
                          <div style={{
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '0.25rem'
                          }}>
                            {formatCurrency(claim.claimAmount)}
                          </div>
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#666'
                          }}>
                            Claimed Amount
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div style={{
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#666'
                          }}>
                            Claim Progress
                          </span>
                          <span style={{
                            fontSize: '0.875rem',
                            color: '#666'
                          }}>
                            {getProgressPercentage(claim.status)}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${getProgressPercentage(claim.status)}%`,
                            height: '100%',
                            backgroundColor: getStatusColor(claim.status),
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      {/* Claim Details Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <label style={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: '600'
                          }}>Incident Date</label>
                          <div style={{ color: '#333' }}>
                            {formatDate(claim.incidentDate)}
                          </div>
                        </div>
                        <div>
                          <label style={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: '600'
                          }}>Submitted</label>
                          <div style={{ color: '#333' }}>
                            {formatDate(claim.dateSubmitted)}
                          </div>
                        </div>
                        {claim.adjusterName && (
                          <div>
                            <label style={{
                              fontSize: '0.875rem',
                              color: '#666',
                              fontWeight: '600'
                            }}>Adjuster</label>
                            <div style={{ color: '#333' }}>
                              {claim.adjusterName}
                            </div>
                          </div>
                        )}
                        {claim.estimatedAmount > 0 && (
                          <div>
                            <label style={{
                              fontSize: '0.875rem',
                              color: '#666',
                              fontWeight: '600'
                            }}>Estimated Amount</label>
                            <div style={{ color: '#333' }}>
                              {formatCurrency(claim.estimatedAmount)}
                            </div>
                          </div>
                        )}
                        {claim.approvedAmount && (
                          <div>
                            <label style={{
                              fontSize: '0.875rem',
                              color: '#666',
                              fontWeight: '600'
                            }}>Approved Amount</label>
                            <div style={{ color: '#28a745', fontWeight: '600' }}>
                              {formatCurrency(claim.approvedAmount)}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Incident Description */}
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}>
                        <label style={{
                          fontSize: '0.875rem',
                          color: '#666',
                          fontWeight: '600',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>Incident Description</label>
                        <div style={{ color: '#333', lineHeight: '1.5' }}>
                          {claim.incidentDescription}
                        </div>
                      </div>

                      {/* Recent Notes */}
                      {claim.notes && claim.notes.length > 0 && (
                        <div>
                          <label style={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: '600',
                            display: 'block',
                            marginBottom: '0.5rem'
                          }}>Recent Updates</label>
                          <div style={{
                            maxHeight: '150px',
                            overflowY: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '1rem'
                          }}>
                            {claim.notes.slice(-3).map((note) => (
                              <div key={note.id} style={{
                                marginBottom: '0.75rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid #f1f3f4'
                              }}>
                                <div style={{
                                  fontSize: '0.875rem',
                                  color: '#333',
                                  marginBottom: '0.25rem'
                                }}>
                                  {note.content}
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: '#666'
                                }}>
                                  {note.authorName} • {formatDate(note.timestamp)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Button */}
                      <div style={{
                        textAlign: 'right',
                        marginTop: '1rem'
                      }}>
                        <button
                          onClick={() => setSelectedClaim(claim)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default ClaimsHistory;